import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Typography, AppBar, Divider
} from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import HearingIcon from '@mui/icons-material/Hearing';
import VideocamIcon from '@mui/icons-material/Videocam';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import TextProcessorView from './views/TextProcessorView';
import TranscriptionView from './views/TranscriptionView';
import VideoToolsView from './views/VideoToolsView';
import Text2VoiceView from './views/Text2VoiceView';
import SequencesView from './views/SequencesView';
import { useWebSocket } from './hooks/useWebSocket';

const drawerWidth = 240;

function AppLayout() {
  const [selectedView, setSelectedView] = useState('textProcessor');
  const [websocketMessage, setWebsocketMessage] = useState(null);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    console.log("WebSocket message received in Layout:", data);
    setWebsocketMessage(data);
  }, []);

  // Conectar al WebSocket
  const { isConnected } = useWebSocket(handleWebSocketMessage);

  const menuItems = [
    { key: 'textProcessor', text: 'Procesador de Texto', icon: <TextFieldsIcon /> },
    { key: 'transcription', text: 'Generar Transcripción', icon: <HearingIcon /> },
    { key: 'videoTools', text: 'Herramientas de Video', icon: <VideocamIcon /> },
    { key: 'text2voice', text: 'Texto a Voz', icon: <RecordVoiceOverIcon /> },
    { key: 'sequences', text: 'Secuencias', icon: <ShuffleIcon /> },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{ width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px` }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap component="div">
            {menuItems.find(item => item.key === selectedView)?.text || 'Masters0Puppets'}
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Toolbar>
          <Typography variant="h6">Masters0Puppets</Typography>
        </Toolbar>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                selected={selectedView === item.key}
                onClick={() => setSelectedView(item.key)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}
      >
        <Toolbar /> {/* Espaciador para el AppBar */}

        {/* Renderiza la vista seleccionada */}
        {selectedView === 'textProcessor' && (
          <TextProcessorView
            latestWebSocketMessage={websocketMessage?.type?.startsWith('text_') || 
                                   websocketMessage?.type === 'progress' || 
                                   websocketMessage?.type === 'queueUpdate' 
                                   ? websocketMessage : null}
            isConnected={isConnected}
          />
        )}
        {selectedView === 'transcription' && (
          <TranscriptionView
            latestWebSocketMessage={websocketMessage?.type?.startsWith('transcription') ? websocketMessage : null}
            isConnected={isConnected}
          />
        )}
        {selectedView === 'videoTools' && (
          <VideoToolsView
            latestWebSocketMessage={websocketMessage?.type?.startsWith('video') ? websocketMessage : null}
            isConnected={isConnected}
          />
        )}
        {selectedView === 'text2voice' && (
          <Text2VoiceView
            latestWebSocketMessage={websocketMessage?.type?.startsWith('text2voice') ? websocketMessage : null}
            isConnected={isConnected}
          />
        )}
        {selectedView === 'sequences' && (
          <SequencesView
            latestWebSocketMessage={websocketMessage?.type?.startsWith('sequenceExecution') ? websocketMessage : null}
            isConnected={isConnected}
          />
        )}
      </Box>
    </Box>
  );
}

export default AppLayout; 